import axios from 'axios';
import type { AxiosRequestConfig } from 'axios';
import { API_AUTH_PATH, API_URL, LOGIN_URL } from 'src/constants';
import UserState, { actions as userActions } from 'src/services/state/User';
import { toSnakeCase } from 'src/utils';

const MAX_CSRF_RETRIES = 2;

interface ExtendedAxiosRequestConfig extends AxiosRequestConfig {
  _csrfRetryCount?: number;
  _skipCsrfRetry?: boolean;
}

const axiosInstance = axios.create({
  baseURL: API_URL,
  headers: { 'content-type': 'application/json' },
  timeout: 1000 * 30,
  withCredentials: true,
});

// Add a request interceptor and transform POST request URL-Params to snake_case.
axiosInstance.interceptors.request.use(
  (config) => {
    config.params = toSnakeCase(config.params);

    return config;
  },
  (error) => Promise.reject(error),
);

function isCsrfError(error: any): boolean {
  if (error?.response?.status !== 403) return false;
  const detail = error?.response?.data?.detail ?? error?.response?.data ?? '';
  return typeof detail === 'string' && detail.toLowerCase().includes('csrf');
}

function getCsrfTokenFromCookie(): string | null {
  const match = document.cookie.match(/csrftoken=([^;]+)/);
  return match ? decodeURIComponent(match[1]) : null;
}

async function refreshCsrfToken(): Promise<string | null> {
  try {
    const { data } = await axiosInstance.get<{ csrf?: string }>(`${API_AUTH_PATH}me/`, {
      ...UserState.authConfig,
      _skipCsrfRetry: true,
    } as ExtendedAxiosRequestConfig);
    return data?.csrf ?? getCsrfTokenFromCookie();
  } catch (refreshError: any) {
    if (refreshError?.response?.status === 401 || refreshError?.response?.status === 403) {
      const next = `${window.location.pathname}${window.location.search}`;
      window.location.replace(`${LOGIN_URL}&next_frontend=${encodeURIComponent(next)}`);
    }
    return null;
  }
}

axiosInstance.interceptors.response.use(
  (response) => response,
  async (error) => {
    const config = error.config as ExtendedAxiosRequestConfig;

    if (!isCsrfError(error) || config._skipCsrfRetry) {
      return Promise.reject(error);
    }

    config._csrfRetryCount = (config._csrfRetryCount ?? 0) + 1;

    if (config._csrfRetryCount > MAX_CSRF_RETRIES) {
      return Promise.reject(error);
    }

    const newToken = await refreshCsrfToken();
    if (!newToken) {
      return Promise.reject(error);
    }

    config.headers = { ...config.headers, 'X-CSRFToken': newToken };

    await userActions.updateCsrfToken(newToken);

    return axiosInstance(config);
  },
);

export default axiosInstance;
